import { Component } from 'vue'
import EventsService from '~/services/events'
import {
	DialogActionConfig,
	DialogBottomSheetType,
	DialogMobileType,
	DialogSetup,
	DialogSize,
} from './dialogTypes'
import { TranslationService } from '~/services/Translation'

class DialogController {
	private acceptEvent: string
	private closeEvent: string

	constructor(private dialog: DialogSetup) {
		this.acceptEvent = this.generateAcceptEvent()
		this.closeEvent = this.generateCloseEvent()
	}

	public open(data?: Record<string, any>) {
		if (data) this.addDataToProps(data)
		EventsService.emitOpenDialog({
			acceptEvent: this.acceptEvent,
			closeEvent: this.closeEvent,
			data,
			...this.dialog,
		})
	}

	public onAccept(callback: (message: any) => void) {
		return EventsService.listen(this.acceptEvent as any, callback)
	}

	public onClose(callback: (message: any) => void) {
		return EventsService.listen(this.closeEvent as any, callback)
	}

	public close() {
		EventsService.emitCloseDialog()
	}

	private generateAcceptEvent() {
		const id = Math.random().toString(16)
		return `dialog-action-${id}-accept`
	}

	private generateCloseEvent() {
		const id = Math.random().toString(16)
		return `dialog-action-${id}-close`
	}

	private addDataToProps(data: Record<string, any>) {
		this.dialog.body = {
			...this.dialog.body,
			componentProps: { ...this.dialog.body?.componentProps, ...data },
		}
	}
}

class DialogBuilder {
	private dialog: DialogSetup = {
		body: {
			interactableBackground: false,
			closeOnBackdropClick: true,
		},
		header: {
			title: undefined,
			closable: true,
		},
		styles: {
			size: 'm',
			mobileType: 'simple',
			bottomSheetType: 'medium',
			borderless: false,
			emptyDialog: false,
		},
		actions: {
			invert: false,
			closeOnAccept: true,
		},
	}

	constructor(component?: Component) {
		if (component) this.dialog.body.component = component
	}

	props(componentProps: Record<string, any>): DialogBuilder {
		this.dialog.body = { ...this.dialog.body, componentProps }
		return this
	}

	bodyText(text: string) {
		this.dialog.body = { ...this.dialog.body, text }
		return this
	}

	size(size: DialogSize): DialogBuilder {
		this.dialog.styles = { ...this.dialog.styles, size }
		return this
	}

	mobileType(mobileType: DialogMobileType): DialogBuilder {
		this.dialog.styles = { ...this.dialog.styles, mobileType }
		return this
	}

	bottomSheetType(bottomSheetType: DialogBottomSheetType): DialogBuilder {
		this.dialog.styles = { ...this.dialog.styles, bottomSheetType }
		return this
	}

	borderless(): DialogBuilder {
		this.dialog.styles = { ...this.dialog.styles, borderless: true }
		return this
	}

	emptyDialog(): DialogBuilder {
		this.dialog.styles = { ...this.dialog.styles, emptyDialog: true }
		return this
	}

	title(title: string): DialogBuilder {
		this.dialog.header = { ...this.dialog.header, title }
		return this
	}

	acceptAction(action?: DialogActionConfig): DialogBuilder {
		this.dialog.actions = { ...this.dialog.actions, accept: action }
		return this
	}

	cancelAction(action?: DialogActionConfig): DialogBuilder {
		this.dialog.actions = { ...this.dialog.actions, cancel: action }
		return this
	}

	invertActions(): DialogBuilder {
		this.dialog.actions = { ...this.dialog.actions, invert: true }
		return this
	}

	interactableBackground(): DialogBuilder {
		this.dialog.body = { ...this.dialog.body, interactableBackground: true }
		return this
	}

	closeOnBackdropClick(mode: boolean): DialogBuilder {
		this.dialog.body = { ...this.dialog.body, closeOnBackdropClick: mode }
		return this
	}

	closeOnAccept(mode: boolean): DialogBuilder {
		this.dialog.actions = { ...this.dialog.actions, closeOnAccept: mode }
		return this
	}

	closeButton(closable: boolean): DialogBuilder {
		this.dialog.header = { ...this.dialog.header, closable }
		return this
	}

	build(): DialogSetup {
		return this.dialog
	}
}

enum PREDEFINED_DIALOG_TYPE {
	DEFAULT = 'default',
	EMPTY = 'empty',
	DELETE_ALERT = 'delete_alert',
}

class PredefinedDialog extends DialogBuilder {
	constructor(type: PREDEFINED_DIALOG_TYPE, component?: Component) {
		super(component)
		this.applyConfig(type)
	}

	private applyConfig(type: PREDEFINED_DIALOG_TYPE) {
		this.mobileType('bottomSheet')
		const configMap: Record<PREDEFINED_DIALOG_TYPE, () => void> = {
			[PREDEFINED_DIALOG_TYPE.EMPTY]: () => this.empty(),
			[PREDEFINED_DIALOG_TYPE.DELETE_ALERT]: () => this.deleteAlert(),
			[PREDEFINED_DIALOG_TYPE.DEFAULT]: () => this.default(),
		}
		const configMethod = configMap[type]
		if (configMethod) configMethod()
	}

	private default(): void {
		this.acceptAction()
		this.cancelAction()
		this.title('')
	}

	private empty(): void {
		this.borderless()
	}

	private deleteAlert(): void {
		this.size('s')
		this.invertActions()
		this.acceptAction({
			text: TranslationService.getInstance().translate('common.action.delete'),
			buttonType: 'filled',
			color: 'error',
		})
		this.cancelAction({
			text: TranslationService.getInstance().translate('common.action.cancel'),
			buttonType: 'outlined',
			color: 'neutral',
		})
	}
}

export {
	DialogController,
	DialogBuilder,
	PredefinedDialog,
	PREDEFINED_DIALOG_TYPE as PredefinedDialogType,
}
